/**
 * Account Service API Client
 * Handles account creation and user management via dedicated Lambda service
 */

import type { ProcessingType } from '@/types';
import { logger, NetworkError, ProcessingError } from '@/utils/logger';

const ACCOUNT_SERVICE_URL = import.meta.env.VITE_ACCOUNT_SERVICE_URL;

if (!ACCOUNT_SERVICE_URL) {
  throw new Error('VITE_ACCOUNT_SERVICE_URL environment variable is required');
}

interface AccountInvitePayload {
  email: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  jobId?: string;
  processingType?: ProcessingType;
  password?: string;
}

interface AccountInviteResponse {
  success: boolean;
  inviteSent?: boolean;
  requiresConfirmation?: boolean;
  autoConfirmed?: boolean;
  passwordSet?: boolean;
  userExists?: boolean;
  message?: string;
  error?: string;
  errorCode?: string;
}

interface GetUserResponse {
  success: boolean;
  userExists: boolean;
  userStatus?: string;
  email?: string;
  name?: string;
  companyName?: string;
  error?: string;
}

export class AccountServiceAPI {
  private async post<T>(payload: Record<string, unknown>): Promise<T> {
    logger.debug('Account Service API Request:', payload);

    try {
      const response = await fetch(ACCOUNT_SERVICE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();

      if (!response.ok) {
        logger.error(`Account Service request failed: ${response.status}`, responseText);
        throw new NetworkError(
          `Account Service request failed: ${response.status} ${response.statusText}`,
          response.status,
          responseText
        );
      }

      const data = JSON.parse(responseText) as T;
      logger.debug('Account Service API Response:', data);
      return data;
    } catch (error) {
      if (error instanceof NetworkError) {
        throw error;
      }

      logger.error('Account Service API error:', error);
      throw new ProcessingError('Failed to communicate with Account Service');
    }
  }

  /**
   * Create account invite and send verification email via Cognito
   */
  async createAccountInvite(payload: AccountInvitePayload): Promise<AccountInviteResponse> {
    try {
      logger.info('Creating account invite:', { email: payload.email, companyName: payload.companyName });

      const response = await this.post<AccountInviteResponse>({
        operation: 'create_account_invite',
        ...payload,
      });

      if (!response.success) {
        const error = new ProcessingError(response.error ?? 'Failed to create account invite');
        (error as ProcessingError & { errorCode?: string }).errorCode = response.errorCode;
        throw error;
      }

      logger.info('Account invite created successfully:', { email: payload.email });
      return response;
    } catch (error) {
      logger.error('Failed to create account invite:', error);
      throw error;
    }
  }

  /**
   * Check if a user exists in Cognito
   */
  async getUser(email: string): Promise<GetUserResponse> {
    try {
      logger.info('Checking user existence:', { email });

      const response = await this.post<GetUserResponse>({
        operation: 'get_user',
        email,
      });

      return response;
    } catch (error) {
      logger.error('Failed to get user:', error);
      throw error;
    }
  }

  /**
   * Save batch history record after processing completes
   * Creates a batch history entry in DynamoDB with cognitoUserId
   */
  async saveBatchHistory(payload: {
    cognitoUserId: string;
    email: string;
    jobId: string;
    filename: string;
    processingType: ProcessingType;
    totalRecords: number;
    successfulRecords: number;
    failedRecords: number;
    cost: number;
    downloadUrl?: string;
    expiresAt?: string;
    companyName?: string;
  }): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      logger.info('Saving batch history:', {
        cognitoUserId: payload.cognitoUserId,
        jobId: payload.jobId,
        filename: payload.filename
      });

      const response = await this.post<{
        success: boolean;
        message?: string;
        error?: string;
      }>({
        operation: 'save_batch_history',
        cognitoUserId: payload.cognitoUserId,
        email: payload.email,
        jobId: payload.jobId,
        filename: payload.filename,
        processingType: payload.processingType,
        totalRecords: payload.totalRecords,
        successfulRecords: payload.successfulRecords,
        failedRecords: payload.failedRecords,
        cost: payload.cost,
        downloadUrl: payload.downloadUrl,
        expiresAt: payload.expiresAt,
        companyName: payload.companyName,
        timestamp: new Date().toISOString(),
        status: 'completed',
      });

      if (!response.success) {
        throw new ProcessingError(response.error ?? 'Failed to save batch history');
      }

      logger.info('Batch history saved successfully:', { jobId: payload.jobId });
      return response;
    } catch (error) {
      logger.error('Failed to save batch history:', error);
      throw error;
    }
  }

  /**
   * Save search history record after search completes
   * Creates a search history entry in DynamoDB with cognitoUserId
   */
  async saveSearchHistory(payload: {
    cognitoUserId: string;
    email: string;
    searchId: string;
    searchType: string; // Accept all SearchType values (validate, enrich, enrich_validate, scoring, scoring_enrich)
    searchRequest: Record<string, unknown>; // The debtor information searched
    cost: number;
    hasResults: boolean;
    collectabilityScore?: number;
    firstName?: string;
    lastName?: string;
    searchResult?: Record<string, unknown>; // Full search result with recovery scenarios
  }): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      logger.info('Saving search history:', {
        cognitoUserId: payload.cognitoUserId,
        searchId: payload.searchId,
        searchType: payload.searchType,
        hasResults: payload.hasResults,
        hasSearchResult: !!payload.searchResult,
        searchResultKeys: payload.searchResult ? Object.keys(payload.searchResult) : []
      });

      const requestPayload = {
        operation: 'save_search_history',
        cognitoUserId: payload.cognitoUserId,
        email: payload.email,
        searchId: payload.searchId,
        searchType: payload.searchType,
        searchRequest: payload.searchRequest,
        cost: payload.cost,
        hasResults: payload.hasResults,
        collectabilityScore: payload.collectabilityScore,
        firstName: payload.firstName,
        lastName: payload.lastName,
        searchResult: payload.searchResult, // Pass the full result
        timestamp: new Date().toISOString(),
      };

      logger.debug('Full save_search_history payload:', requestPayload);

      const response = await this.post<{
        success: boolean;
        message?: string;
        error?: string;
      }>(requestPayload);

      if (!response.success) {
        throw new ProcessingError(response.error ?? 'Failed to save search history');
      }

      logger.info('Search history saved successfully:', { searchId: payload.searchId });
      return response;
    } catch (error) {
      logger.error('Failed to save search history:', error);
      throw error;
    }
  }

  /**
   * Get search history for a user from DynamoDB
   */
  async getSearchHistory(cognitoUserId: string, limit: number = 50): Promise<Record<string, unknown>[]> {
    try {
      logger.info('Fetching search history from DynamoDB', { cognitoUserId, limit });

      const response = await this.post<{
        success: boolean;
        searches?: Record<string, unknown>[];
        count?: number;
        error?: string;
      }>({
        operation: 'get_search_history',
        cognitoUserId,
        limit,
      });

      if (!response.success) {
        throw new ProcessingError(response.error ?? 'Failed to fetch search history');
      }

      logger.info('Search history fetched successfully', { count: response.searches?.length || 0 });
      return response.searches || [];
    } catch (error) {
      logger.error('Failed to fetch search history:', error);
      throw error;
    }
  }

  /**
   * Get batch history for a user from DynamoDB
   */
  async getBatchHistory(cognitoUserId: string, limit: number = 50): Promise<Record<string, unknown>[]> {
    try {
      logger.info('Fetching batch history from DynamoDB', { cognitoUserId, limit });

      const response = await this.post<{
        success: boolean;
        batches?: Record<string, unknown>[];
        count?: number;
        error?: string;
      }>({
        operation: 'get_batch_history',
        cognitoUserId,
        limit,
      });

      if (!response.success) {
        throw new ProcessingError(response.error ?? 'Failed to fetch batch history');
      }

      logger.info('Batch history fetched successfully', { count: response.batches?.length || 0 });
      return response.batches || [];
    } catch (error) {
      logger.error('Failed to fetch batch history:', error);
      throw error;
    }
  }

  /**
   * Get a fresh download URL for a batch job
   * Generates a new presigned S3 URL valid for 1 hour
   */
  async getDownloadUrl(jobId: string, format: 'json' | 'csv' = 'json'): Promise<{
    downloadUrl: string;
    expiresAt: string;
    downloadFormatsAvailable: string[];
  }> {
    try {
      logger.info('Requesting fresh download URL', { jobId, format });

      const response = await this.post<{
        success: boolean;
        downloadUrl?: string;
        expiresAt?: string;
        downloadFormatsAvailable?: string[];
        error?: string;
      }>({
        operation: 'get_download_url',
        jobId,
        format,
      });

      if (!response.success || !response.downloadUrl) {
        throw new ProcessingError(response.error ?? 'Failed to generate download URL');
      }

      logger.info('Download URL generated successfully', { jobId, expiresAt: response.expiresAt });
      return {
        downloadUrl: response.downloadUrl,
        expiresAt: response.expiresAt || new Date(Date.now() + 3600000).toISOString(), // Default to 1 hour
        downloadFormatsAvailable: response.downloadFormatsAvailable || ['json', 'csv'],
      };
    } catch (error) {
      logger.error('Failed to get download URL:', error);
      throw error;
    }
  }
}

export const accountServiceAPI = new AccountServiceAPI();
